import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { LanguageCode } from '../types/language';
import { isHashedPin, type HashedPin } from '../utils/pin-crypto';
import { APP_SKIN_PRESETS, DEFAULT_APP_SKIN, type AppSkinId } from '../data/parent-skin-presets';
import { DEFAULT_ARCADE_BASE_MINUTES, normalizeArcadeMinutes, useArcadeStore } from './arcadeStore';

export interface ParentalConsent {
  /** ISO timestamp of consent. */
  acceptedAt: string;
  /** The legal-name the parent typed. Treated as an electronic signature. */
  parentLegalName: string;
  /** Version-tagged so a future ToS revision can re-prompt. */
  policyVersion: string;
}

interface SettingsState {
  curriculumStartDate: string;
  themeOverride: string | null;
  /**
   * Parent-picked app skin (color palette + gradient + text tokens).
   * Applied by AppShell as CSS variables on <html>. Independent of the
   * shop reward economy — this is a baseline appearance choice always
   * available in Settings → Appearance, even when shop themes are shelved.
   * See src/data/parent-skin-presets.ts.
   */
  appSkin: AppSkinId;
  setAppSkin: (skin: AppSkinId) => void;
  maxScreenTimeMinutes: number;
  selectedState: string | null;
  darkMode: boolean;
  // Language learning settings (per-child)
  selectedLanguages: Record<string, LanguageCode[]>;
  primaryLanguage: Record<string, LanguageCode>;
  // Parent PIN lock — stored as a PBKDF2 hash, never plaintext.
  // null = no PIN set. See utils/pin-crypto.ts.
  parentPin: HashedPin | null;
  setParentPin: (pin: HashedPin | null) => void;
  // PIN brute-force protection (persisted so reload doesn't reset the counter)
  pinFailedAttempts: number;
  pinLockedUntil: number | null; // epoch ms
  recordPinFailure: () => void;
  resetPinFailures: () => void;
  // Schedule customization
  disabledBlocks: string[];
  /**
   * Per-child parent opt-in for sensitive health content (puberty,
   * reproductive anatomy, substance awareness). Defaults to `false` — these
   * activities are excluded from the feed and selector unless the parent
   * explicitly enables them for a specific child.
   */
  sensitiveHealthContentEnabled: Record<string, boolean>;
  /**
   * Per-child parent opt-in for Music Studio microphone capture (vocal
   * takes, sample-your-own-drum). Default `false`. When disabled, the
   * studio hides mic-dependent surfaces and getUserMedia is never called.
   */
  studioMicEnabled: Record<string, boolean>;
  /**
   * Per-child parent opt-in for sharing/importing song projects between
   * profiles (async jam, .hsmusic file import/export). Default `false`.
   * Local-only feature — no network — but parental gating still applies
   * because shared content can include audio recordings of voice.
   */
  studioSharingEnabled: Record<string, boolean>;
  dailyLessonTarget: number;
  maxGameTimeMinutes: number;
  homeschoolExperience: 'new' | 'some' | 'veteran';
  learningPace: 'weekdays' | '6-days' | 'everyday' | 'flexible';
  onboardingCompletedAt: string | null;
  setDailyLessonTarget: (count: number) => void;
  setMaxGameTime: (minutes: number) => void;
  setHomeschoolExperience: (level: 'new' | 'some' | 'veteran') => void;
  setLearningPace: (pace: 'weekdays' | '6-days' | 'everyday' | 'flexible') => void;
  setOnboardingCompletedAt: (ts: string | null) => void;
  sportsMathMode: boolean;
  toggleSportsMathMode: () => void;
  /**
   * Parent-controlled visibility of XP, levels, and reward
   * celebrations in the kid-facing UI. Default `true` to preserve current
   * behavior. When `false`, all reward chrome (TopBar pills, popups,
   * level-up modal, end-of-activity chips, and reward prompts)
   * is hidden — but XP/progress tracking continues unchanged in the data layer
   * so flipping back on restores accumulated totals.
   */
  showRewards: boolean;
  setShowRewards: (enabled: boolean) => void;
  /**
   * Parent-controlled visibility of streak counters and milestones in the
   * kid-facing UI. Split from `showRewards` so families can keep streaks
   * (habit tracking) while hiding XP/reward chrome, or vice
   * versa. Default `true`. Streak data continues to accumulate when off.
   */
  showStreaks: boolean;
  setShowStreaks: (enabled: boolean) => void;
  // Cloud sync
  cloudSyncEnabled: boolean;
  /** Optional hint the parent can write to remember their passphrase. Never the passphrase itself. */
  syncPassphraseHint: string;
  /** ISO timestamp of last successful full sync cycle. */
  lastSyncAt: string | null;
  setCloudSyncEnabled: (enabled: boolean) => void;
  setSyncPassphraseHint: (hint: string) => void;
  setLastSyncAt: (ts: string | null) => void;
  /** COPPA verifiable parental consent record. Captured before the first child
   *  profile is created. Storing the timestamp + parent's affirmed legal name
   *  + version of the policies they accepted is the electronic-signature record
   *  for COPPA / state-level (e.g., CCPA) audits. */
  parentalConsent: ParentalConsent | null;
  recordParentalConsent: (consent: ParentalConsent) => void;
  /** Sentry telemetry opt-out (parent-controlled, per the Privacy Policy). */
  errorReportingEnabled: boolean;
  setErrorReportingEnabled: (enabled: boolean) => void;
  setCurriculumStartDate: (date: string) => void;
  setThemeOverride: (themeId: string | null) => void;
  setMaxScreenTime: (minutes: number) => void;
  setSelectedState: (stateCode: string | null) => void;
  setDarkMode: (enabled: boolean) => void;
  setSelectedLanguages: (childId: string, langs: LanguageCode[]) => void;
  setPrimaryLanguage: (childId: string, lang: LanguageCode) => void;
  setDisabledBlocks: (blocks: string[]) => void;
  setSensitiveHealthContentEnabled: (childId: string, enabled: boolean) => void;
  setStudioMicEnabled: (childId: string, enabled: boolean) => void;
  setStudioSharingEnabled: (childId: string, enabled: boolean) => void;
}

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set) => ({
      curriculumStartDate: '2026-03-06',
      themeOverride: null,
      appSkin: DEFAULT_APP_SKIN,
      setAppSkin: (skin) =>
        set({ appSkin: APP_SKIN_PRESETS[skin] ? skin : DEFAULT_APP_SKIN }),
      maxScreenTimeMinutes: 30,
      selectedState: null,
      darkMode: false,
      parentPin: null,
      pinFailedAttempts: 0,
      pinLockedUntil: null,
      selectedLanguages: {},
      primaryLanguage: {},
      disabledBlocks: [],
      sensitiveHealthContentEnabled: {},
      studioMicEnabled: {},
      studioSharingEnabled: {},
      dailyLessonTarget: 5,
      maxGameTimeMinutes: DEFAULT_ARCADE_BASE_MINUTES,
      homeschoolExperience: 'new' as const,
      learningPace: 'weekdays' as const,
      onboardingCompletedAt: null,
      setDailyLessonTarget: (count) => set({ dailyLessonTarget: Math.max(1, Math.min(11, count)) }),
      setMaxGameTime: (minutes) => {
        const maxGameTimeMinutes = normalizeArcadeMinutes(minutes);
        useArcadeStore.getState().setBaseDailyMinutes(maxGameTimeMinutes);
        set({ maxGameTimeMinutes });
      },
      setHomeschoolExperience: (level) => set({ homeschoolExperience: level }),
      setLearningPace: (pace) => set({ learningPace: pace }),
      setOnboardingCompletedAt: (ts) => set({ onboardingCompletedAt: ts }),
      sportsMathMode: false,
      showRewards: true,
      showStreaks: true,
      cloudSyncEnabled: true,
      syncPassphraseHint: '',
      lastSyncAt: null,
      parentalConsent: null,
      errorReportingEnabled: true,
      toggleSportsMathMode: () => set(s => ({ sportsMathMode: !s.sportsMathMode })),
      setShowRewards: (enabled) => set({ showRewards: enabled }),
      setShowStreaks: (enabled) => set({ showStreaks: enabled }),
      setCloudSyncEnabled: (enabled) => set({ cloudSyncEnabled: enabled }),
      setSyncPassphraseHint: (hint) => set({ syncPassphraseHint: hint }),
      setLastSyncAt: (ts) => set({ lastSyncAt: ts }),
      recordParentalConsent: (consent) => set({ parentalConsent: consent }),
      setErrorReportingEnabled: (enabled) => set({ errorReportingEnabled: enabled }),
      setCurriculumStartDate: (date) => set({ curriculumStartDate: date }),
      setThemeOverride: (themeId) => set({ themeOverride: themeId }),
      setMaxScreenTime: (minutes) => set({ maxScreenTimeMinutes: minutes }),
      setSelectedState: (stateCode) => set({ selectedState: stateCode }),
      setDarkMode: (enabled) => set({ darkMode: enabled }),
      setSelectedLanguages: (childId, langs) =>
        set(s => {
          const newSelected = { ...s.selectedLanguages, [childId]: langs };
          const newPrimary = { ...s.primaryLanguage };

          if (langs.length === 0) {
            // Clear primary when all languages removed
            delete newPrimary[childId];
          } else if (!newPrimary[childId] || !langs.includes(newPrimary[childId])) {
            // Auto-set primary to first if not yet set or current primary was removed
            newPrimary[childId] = langs[0];
          }

          return { selectedLanguages: newSelected, primaryLanguage: newPrimary };
        }),
      setPrimaryLanguage: (childId, lang) =>
        set(s => ({ primaryLanguage: { ...s.primaryLanguage, [childId]: lang } })),
      setParentPin: (pin) =>
        set({ parentPin: pin, pinFailedAttempts: 0, pinLockedUntil: null }),
      recordPinFailure: () =>
        set(s => {
          const attempts = s.pinFailedAttempts + 1;
          // Exponential backoff after 5 wrong attempts: 30s, 1m, 5m, 15m, 1h
          const ladder = [30, 60, 300, 900, 3600];
          let lockedUntil: number | null = null;
          if (attempts >= 5) {
            const idx = Math.min(attempts - 5, ladder.length - 1);
            lockedUntil = Date.now() + ladder[idx] * 1000;
          }
          return { pinFailedAttempts: attempts, pinLockedUntil: lockedUntil };
        }),
      resetPinFailures: () => set({ pinFailedAttempts: 0, pinLockedUntil: null }),
      setDisabledBlocks: (blocks) => set({ disabledBlocks: blocks }),
      setSensitiveHealthContentEnabled: (childId, enabled) =>
        set(s => ({
          sensitiveHealthContentEnabled: {
            ...s.sensitiveHealthContentEnabled,
            [childId]: enabled,
          },
        })),
      setStudioMicEnabled: (childId, enabled) =>
        set(s => ({
          studioMicEnabled: { ...s.studioMicEnabled, [childId]: enabled },
        })),
      setStudioSharingEnabled: (childId, enabled) =>
        set(s => ({
          studioSharingEnabled: { ...s.studioSharingEnabled, [childId]: enabled },
        })),
    }),
    {
      name: 'homeschool-settings',
      version: 10,
      migrate: (persisted: unknown, fromVersion: number) => {
        const state = (persisted ?? {}) as Record<string, unknown>;
        if (fromVersion < 2) {
          if (state.parentPin && !isHashedPin(state.parentPin)) {
            state.parentPin = null;
          }
          state.pinFailedAttempts = 0;
          state.pinLockedUntil = null;
        }
        if (fromVersion < 3) {
          // v2 → v3: add cloud sync fields with safe defaults
          state.cloudSyncEnabled = false;
          state.syncPassphraseHint = '';
          state.lastSyncAt = null;
        }
        if (fromVersion < 4) {
          // v3 → v4: add COPPA consent record + error-reporting opt-in.
          // Existing accounts default to consented (they were created under
          // the prior implicit-consent model) but errorReporting defaults on.
          state.parentalConsent = null;
          state.errorReportingEnabled = true;
        }
        if (fromVersion < 5) {
          // v4 → v5: Music Studio v2 — per-child parent opt-in for
          // microphone capture and project sharing. Default off; the parent
          // dashboard exposes toggles in the Studio Safety panel.
          state.studioMicEnabled = {};
          state.studioSharingEnabled = {};
        }
        if (fromVersion < 6) {
          state.showRewards = true;
          state.showStreaks = true;
        }
        if (fromVersion < 7) {
          state.dailyLessonTarget = 5;
          state.maxGameTimeMinutes = DEFAULT_ARCADE_BASE_MINUTES;
          state.homeschoolExperience = 'new';
          state.learningPace = 'weekdays';
          state.onboardingCompletedAt = null;
        }
        if (fromVersion < 8) {
          // v7 → v8: parent-picked app skin (blue/green/yellow/purple/pink/
          // orange/red/dark-blue). Default to the readability-improved blue
          // for all existing accounts. Existing equipped shop themes still
          // take precedence in AppShell when the reward economy is on.
          state.appSkin = DEFAULT_APP_SKIN;
        }
        if (fromVersion < 9) {
          // v8 → v9: cloud sync is now always-on (plaintext fallback when
          // no E2E key is present). Flip existing accounts so child profiles
          // and progress follow the account across devices.
          state.cloudSyncEnabled = true;
        }
        if (fromVersion < 10) {
          state.maxGameTimeMinutes = normalizeArcadeMinutes(
            typeof state.maxGameTimeMinutes === 'number'
              ? state.maxGameTimeMinutes
              : DEFAULT_ARCADE_BASE_MINUTES,
          );
        }
        return state as unknown as SettingsState;
      },
      onRehydrateStorage: () => (state) => {
        useArcadeStore.getState().setBaseDailyMinutes(
          normalizeArcadeMinutes(state?.maxGameTimeMinutes ?? DEFAULT_ARCADE_BASE_MINUTES),
        );
      },
    }
  )
);
